import React, { useEffect, useRef, useState } from 'react';
import { useMapStore, useUIStore } from '../../stores';
import { MapService } from '../../services/map/mapService';
import { logger } from '../../../../shared/utils/logger';

interface MapComponentProps {
  className?: string;
  style?: React.CSSProperties;
  onMapServiceReady?: (service: MapService) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ 
  className = '', 
  style = {},
  onMapServiceReady
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapService, setMapService] = useState<MapService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Stores V2 independientes
  const { 
    center, 
    zoom, 
    isLoading, 
    error,
    setInitialized, 
    setLoading, 
    setError 
  } = useMapStore();
  
  const { addNotification } = useUIStore();

  // Generar ID único para el mapa
  const mapId = `map-${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    if (!mapContainerRef.current || isInitialized) return; // Prevent re-initialization

    const initMap = async () => {
      try {
        setLoading(true);
        setError(null);

        if (mapService) { // Limpiar cualquier mapa anterior
          try { mapService.destroy(); } catch (err) { console.error('Error al destruir mapa anterior:', err); }
        }
        mapContainerRef.current!.id = mapId; // Asignar ID al contenedor del mapa
        const newMapService = new MapService({ target: mapId, center: center, zoom: zoom }); // Usar el ID
        await newMapService.initMap();
        
        setMapService(newMapService);
        setIsInitialized(true); // Mark as initialized
        setInitialized(true);
        
        // TODO: Implementar sistema de eventos para el MapService
        // Los listeners se añadirán cuando el sistema de eventos esté completo
        logger.log('MAP_SERVICE', 'MapService inicializado correctamente');
        
        // Notificar que el mapService está listo
        if (onMapServiceReady) {
          onMapServiceReady(newMapService);
        }
        
      } catch (err) {
        console.error('Error al inicializar mapa V2:', err);
        setError(err instanceof Error ? err.message : 'Error desconocido');
        addNotification({ 
          type: 'error', 
          title: 'Error V2', 
          message: 'Error al inicializar mapa V2' 
        });
      } finally {
        setLoading(false);
      }
    };
    
    initMap();
    
    return () => {
      if (mapService) {
        try {
          mapService.destroy();
        } catch (err) {
          console.error('Error al destruir mapa V2:', err);
        }
      }
    };
  }, [isInitialized, onMapServiceReady]); // Only run when isInitialized changes

  const handleRetry = () => {
    setIsInitialized(false);
    setMapService(null);
    setError(null);
  };

  if (error) {
    return (
      <div 
        className={`map-component-error ${className}`}
        style={{
          ...style,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '20px'
        }}
      >
        <h3>Error al cargar mapa</h3>
        <p style={{ color: '#dc3545', marginBottom: '15px' }}>{error}</p>
        <button 
          onClick={handleRetry}
          style={{
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div 
      className={`map-component-v2 ${className}`}
      style={{
        ...style,
        position: 'relative',
        height: '100%',
        minHeight: '400px'
      }}
    >
      {/* Contenedor del mapa */}
      <div 
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          zIndex: 1
        }}
      />
      
      {/* Indicador de carga */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '10px 20px',
          borderRadius: '4px',
          zIndex: 10
        }}>
          Cargando mapa...
        </div>
      )}
      
    </div>
  );
};

export default MapComponent;
